import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError } from '@/lib/api.ts';

/**
 * The small shared pieces of the admin screens.
 *
 * Here rather than in `ui/` because none of them are for the living room.
 * They assume a pointer, a keyboard and a person sitting eighteen inches from
 * the screen, which is the opposite of every other component in this app.
 */

/**
 * A refusal, shown where it happened.
 *
 * SAYS WHAT THE SERVER SAID. Every 409 in the admin API is written to be read
 * by a person — "Channel 206 is already Sky Sports", "That range overlaps
 * MLB" — and replacing those with "Something went wrong" throws away the only
 * part that tells somebody what to do next.
 */
export function Problem({ error, children }: { error: unknown; children?: ReactNode }) {
  if (!error) return null;
  const message = error instanceof ApiError
    ? error.message
    : (error as Error)?.message ?? 'Something went wrong';
  return <p className="admin-error">{message}{children}</p>;
}

/** A labelled input. The label is a real <label>, so the click target is both. */
export function Field({
  label, hint, children,
}: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="admin-field">
      <span className="admin-field-label">{label}</span>
      {children}
      {hint && <span className="admin-field-hint">{hint}</span>}
    </label>
  );
}

/** A small word on a coloured ground: a kind, a state, a count. */
export function Badge({ tone, children }: { tone?: 'good' | 'warn' | 'off'; children: ReactNode }) {
  return <span className={`admin-badge${tone ? ` is-${tone}` : ''}`}>{children}</span>;
}

/**
 * On/off, as a switch rather than a checkbox.
 *
 * Its own component because availability is the single most-pressed control
 * on these screens and a bare checkbox gives no sense of which way is on
 * until you read the column heading.
 */
export function Switch({
  on, onChange, disabled, label,
}: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={`admin-switch${on ? ' is-on' : ''}`}
      disabled={disabled}
      onClick={() => onChange(!on)}
    >
      <span className="admin-switch-knob" />
    </button>
  );
}

/** "3 hours ago", or the date once that stops being useful. */
export function when(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 'never';
  const mins = Math.round((Date.now() - then) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days}d ago`;
  return new Date(then).toLocaleDateString();
}

/**
 * A delete that takes two presses.
 *
 * NOT A MODAL, deliberately. A dialog steals focus, has to be dismissed, and
 * trains people to press the second button without reading — which is the
 * opposite of what a confirmation is for. Arming in place keeps the question
 * next to the thing it is about, and walking away answers "no".
 *
 * The timeout matters as much as the second press: an armed button that stays
 * armed is a landmine for whoever scrolls past it in four minutes.
 */
export function ConfirmButton({
  onConfirm, disabled, children, armedLabel = 'Really?',
}: {
  onConfirm: () => void;
  disabled?: boolean;
  children: ReactNode;
  armedLabel?: string;
}) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);

  return (
    <button
      type="button"
      className={armed ? 'btn admin-armed' : 'btn ghost'}
      disabled={disabled}
      onClick={() => {
        if (!armed) { setArmed(true); return; }
        setArmed(false);
        onConfirm();
      }}
    >
      {armed ? armedLabel : children}
    </button>
  );
}
